/**
 * NodePalette.tsx - Sidebar palette for dragging step types onto the canvas
 *
 * Displays all available automation step types with category filtering.
 * Uses drag-and-drop to add nodes to the flow canvas.
 *
 * Related:
 *   - components/paletteStepTypes.ts - Shared step type definitions
 *   - components/StepPaletteModal.tsx - Modal version of the palette
 *   - components/StepIcon.tsx - Icon component for step types
 */

import { css } from 'aphrodite';
import { type DragEvent, useState } from 'react';
import { type PaletteCategory, categoryFilters, paletteNodes } from './paletteStepTypes';
import { StepIcon } from './StepIcon';
import { styleSheet } from './styles/NodePaletteStyles';

interface NodePaletteProps {
	excludedStepTypes?: string[];
}

export function NodePalette({ excludedStepTypes }: NodePaletteProps) {
	const [activeCategory, setActiveCategory] = useState<PaletteCategory>('all');

	const onDragStart = (event: DragEvent<HTMLDivElement>, nodeType: string) => {
		if (!event.dataTransfer) {
			return;
		}
		event.dataTransfer.setData('application/reactflow', nodeType);
		event.dataTransfer.effectAllowed = 'move';
	};

	const availableNodes = excludedStepTypes?.length
		? paletteNodes.filter(node => !excludedStepTypes.includes(node.type))
		: paletteNodes;

	const filteredNodes =
		activeCategory === 'all' ? availableNodes : availableNodes.filter(node => node.paletteCategory === activeCategory);

	return (
		<div className={css(styleSheet.container)}>
			<h3 className={css(styleSheet.title)}>Step Palette</h3>
			<p className={css(styleSheet.description)}>Drag steps onto the canvas to build your automation</p>

			{/* Category filters */}
			<div className={css(styleSheet.filterContainer)}>
				{categoryFilters.map(filter => (
					<button
						key={filter.id}
						type='button'
						className={css(styleSheet.filterButton, activeCategory === filter.id && styleSheet.filterButtonActive)}
						onClick={() => setActiveCategory(filter.id)}
					>
						{filter.label}
					</button>
				))}
			</div>

			<div className={css(styleSheet.nodeList)}>
				{filteredNodes.map(node => (
					<div
						key={node.type}
						className={css(
							styleSheet.paletteNode,
							styleSheet[`node_${node.visualCategory}` as keyof typeof styleSheet]
						)}
						draggable
						onDragStart={event => onDragStart(event, node.type)}
						role='button'
						tabIndex={0}
						aria-label={`Drag ${node.label} step`}
					>
						<div className={css(styleSheet.nodeHeader)}>
							<StepIcon category={node.visualCategory} />
							<span className={css(styleSheet.nodeLabel)}>{node.label}</span>
						</div>
						<div className={css(styleSheet.nodeDescription)}>{node.description}</div>
					</div>
				))}
			</div>
		</div>
	);
}
